import { CardContainer } from '../../StyledComponents/Card';
import CondorSpinner from '../CondorSpinner';
import { IconButton } from '@mui/material';
import { Download } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { getDeviceTags } from '../../services/DevicesService';
import { SectionTitleText } from '../../StyledComponents/Typography';

const PAGE_SIZE = 1000;

const fetchAllDeviceTags = async (tagName: string) => {
  let allResults: any[] = [];
  let next: string | undefined = undefined;

  do {
    const data = await getDeviceTags({
      tagName,
      limit: PAGE_SIZE,
      nextToken: next,
    });
    allResults = [...allResults, ...(data?.results ?? [])];
    next = data?.next;
  } while (next);

  return { results: allResults, count: allResults.length };
};

export const CondorStandardTags = (props: {
  standardTags: { tagName: string }[];
}) => {
  const { standardTags } = props;

  const tag0 = standardTags[0] ?? { tagName: '' };
  const tag1 = standardTags[1] ?? { tagName: '' };

  const { data: data0, isPending: pending0 } = useQuery({
    queryKey: ['condor-standard-tags', tag0.tagName],
    queryFn: () => fetchAllDeviceTags(tag0.tagName),
    enabled: !!tag0.tagName,
  });

  const { data: data1, isPending: pending1 } = useQuery({
    queryKey: ['condor-standard-tags', tag1.tagName],
    queryFn: () => fetchAllDeviceTags(tag1.tagName),
    enabled: !!tag1.tagName,
  });

  const deviceCounts = [data0, data1].map((d) => d?.results?.length ?? 0);
  const totalDevices = deviceCounts.reduce((a, b) => a + b, 0);

  const tagEntries = [
    {
      tag: tag0,
      data: data0,
      deviceCount: deviceCounts[0],
      percentage:
        totalDevices > 0
          ? ((deviceCounts[0] / totalDevices) * 100).toFixed(1)
          : '0.0',
    },
    {
      tag: tag1,
      data: data1,
      deviceCount: deviceCounts[1],
      percentage:
        totalDevices > 0
          ? ((deviceCounts[1] / totalDevices) * 100).toFixed(1)
          : '0.0',
    },
  ];

  const isLoading = pending0 || pending1;

  const handleDownload = (tag: { tagName: string }, data: any) => {
    const serialNumbers = data?.results
      ?.map((dt: any) => dt?.serialNumber)
      .filter((sn: any): sn is string => !!sn);

    if (!serialNumbers || serialNumbers.length === 0) {
      console.error('No serial numbers found for tag: ' + tag.tagName);
      return;
    }

    const csvContent = ['Serial Number', ...serialNumbers].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', `${tag.tagName}-serial-numbers.csv`);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div style={{ display: 'flex' }}>
      <div style={{ flexBasis: '20%' }}></div>
      <div style={{ flexBasis: '60%' }}>
        <div style={{ paddingLeft: '16px' }}>
          <SectionTitleText>Standard Tags</SectionTitleText>
        </div>
        <CardContainer>
          {isLoading ? (
            <CondorSpinner />
          ) : (
            <div>
              {tagEntries.map(({ tag, data, deviceCount, percentage }) => (
                <div
                  key={tag.tagName}
                  style={{
                    display: 'flex',
                    marginBottom: '16px',
                    alignItems: 'center',
                  }}
                >
                  <div style={{ flexBasis: '35%', fontSize: '1rem' }}>
                    {tag.tagName}
                  </div>
                  <div
                    style={{
                      flexBasis: '50%',
                      fontSize: '1rem',
                      fontWeight: 'bold',
                    }}
                  >
                    {deviceCount} devices ({percentage}%)
                  </div>
                  <div style={{ flexBasis: '15%', textAlign: 'right' }}>
                    <IconButton
                      onClick={() => handleDownload(tag, data)}
                      aria-label={`download-${tag.tagName}-serial-numbers`}
                      title={`Download ${tag.tagName} Serial Numbers`}
                      disabled={!data?.results?.length}
                    >
                      <Download />
                    </IconButton>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContainer>
      </div>
      <div style={{ flexBasis: '20%' }}></div>
    </div>
  );
};
